/**
 * @file components/home/ContactSection.tsx
 * @description 對外公開首頁「聯絡我們」分區。
 *
 * 設計目標：
 *   1. 補齊首頁缺失的聯絡入口 — 過去 `#contact` 直接指向 footer，僅有兩條連結。
 *   2. 左欄完整列出對外可公開的聯絡資訊（電話 / Email / 地址 / 營業時間），
 *      並提供 tel: / mailto: 直達連結，方便手機用戶一鍵撥打。
 *   3. 右欄提供諮詢表單入口（姓名 / Email / 電話 / 諮詢方案 / 留言），
 *      讓潛在客戶不必離開頁面即可完成第一次接洽。
 *   4. 視覺風格沿用首頁「Quiet Refinement」基調 — 純 inline style + CSS
 *      變數，未引入 antd，與相鄰 section 對齊。
 *
 * TODO（後端對接）：目前 `submit()` 為前端模擬，僅做必填校驗 + 切換到
 *   「感謝頁」狀態。待後端新增 POST /api/public/inquiries（公開、無需登入、
 *   建議加 reCAPTCHA）後，將 fetch 邏輯接入即可，UI 不需要再改。
 */

import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, type LucideIcon } from 'lucide-react';

/** 諮詢方案下拉選項 — 與首頁 Schemes 區的三套標準一致，外加「其他」兜底。 */
const INQUIRY_TOPICS = [
  { value: '', label: '請選擇諮詢主題（選填）' },
  { value: 'TOP', label: 'TOP 有機驗證' },
  { value: 'TAP', label: 'TAP 產銷履歷' },
  { value: 'ECO', label: 'ECO 友善農業' },
  { value: 'OTHER', label: '其他諮詢' },
] as const;

/** 對外可公開的聯絡資訊條目。值如後續調整，集中改這裡即可。 */
const CONTACT_ITEMS: ReadonlyArray<{
  icon: LucideIcon;
  label: string;
  value: string;
  href?: string;
  hint?: string;
}> = [
  {
    icon: Phone,
    label: '客服專線',
    value: '049-2568787 / 049-2568585',
    href: 'tel:0492568787',
    hint: '傳真 049-2566660',
  },
  {
    icon: Mail,
    label: '客服信箱',
    value: 'zh.cert@msa.hinet.net',
    href: 'mailto:zh.cert@msa.hinet.net',
    hint: 'coaatw@gmail.com',
  },
  {
    icon: MapPin,
    label: '公司地址',
    value: '542 南投縣草屯鎮中正路 486-11 號',
    hint: '請事先預約以便安排接待',
  },
  {
    icon: Clock,
    label: '營業時間',
    value: '週一至週五 08:30–17:30',
    hint: '固定假日及週末公休',
  },
];

/**
 * 表單欄位的本地狀態。所有欄位皆為字串，提交前做 trim 校驗。
 */
interface FormState {
  name: string;
  email: string;
  phone: string;
  topic: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  name: '',
  email: '',
  phone: '',
  topic: '',
  message: '',
};

/** 簡易 Email 校驗 — 僅做「含 @ 與 .」基本格式檢查，避免過度限制。 */
function isValidEmail(value: string): boolean {
  return /.+@.+\..+/.test(value.trim());
}

export function ContactSection() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  /** 通用 onChange — 同步更新單一欄位並清除該欄位既有錯誤。 */
  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = '請填寫您的稱呼';
    if (!form.email.trim()) next.email = '請填寫聯絡 Email';
    else if (!isValidEmail(form.email)) next.email = 'Email 格式不正確';
    if (!form.message.trim()) next.message = '請簡述您的諮詢需求';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // TODO: 對接後端 — POST /api/public/inquiries
      // 目前僅模擬網路延遲，提交後切換到「感謝」視圖。
      await new Promise((resolve) => setTimeout(resolve, 600));
      setSubmitted(true);
    } catch (err) {
      // 預留：對接 API 後在此記錄與提示
      console.error('[ContactSection] submit failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setSubmitted(false);
  };

  return (
    <section
      id="contact"
      style={{
        padding: '96px 0',
        background: 'var(--surface-base)',
        borderTop: '1px solid var(--stone-200)',
        borderBottom: '1px solid var(--stone-200)',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 32px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* ===== Section header ===== */}
        <div style={{ maxWidth: 720 }}>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(28px, 3vw, 36px)',
              fontWeight: 600,
              letterSpacing: '-0.015em',
              color: 'var(--stone-900)',
              lineHeight: 1.2,
              margin: 0,
            }}
          >
            聯絡我們
          </h2>
          <p
            style={{
              marginTop: 14,
              fontSize: 15,
              lineHeight: 1.65,
              color: 'var(--stone-600)',
              maxWidth: 620,
            }}
          >
            無論您是準備申請驗證的生產者，或是希望進一步了解我們服務範圍的合作夥伴，
            都歡迎透過下方表單留下訊息，或直接以電話 / Email 與我們聯繫。
          </p>
        </div>

        {/* ===== Two columns: info + form ===== */}
        <div
          style={{
            marginTop: 56,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 0.85fr) minmax(0, 1.15fr)',
            gap: 48,
            alignItems: 'start',
          }}
        >
          {/* ---- Left: contact info list ---- */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {CONTACT_ITEMS.map((item) => (
              <ContactInfoRow key={item.label} item={item} />
            ))}
          </div>

          {/* ---- Right: form / thank-you card ---- */}
          <div
            style={{
              padding: 32,
              background: 'var(--surface-canvas)',
              border: '1px solid var(--stone-200)',
              borderRadius: 14,
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}
          >
            {submitted ? (
              <ThankYouCard onReset={resetForm} />
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void submit();
                }}
                noValidate
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 16,
                  }}
                >
                  <FormField label="您的稱呼" required error={errors.name}>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      placeholder="王先生 / 王小姐"
                      style={inputStyle(Boolean(errors.name))}
                    />
                  </FormField>

                  <FormField label="聯絡 Email" required error={errors.email}>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      placeholder="you@example.com"
                      style={inputStyle(Boolean(errors.email))}
                    />
                  </FormField>

                  <FormField label="聯絡電話">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                      placeholder="選填"
                      style={inputStyle(false)}
                    />
                  </FormField>

                  <FormField label="諮詢主題">
                    <select
                      value={form.topic}
                      onChange={(e) => setField('topic', e.target.value)}
                      style={{
                        ...inputStyle(false),
                        appearance: 'none',
                        backgroundImage:
                          "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 14px center',
                        paddingRight: 36,
                        cursor: 'pointer',
                      }}
                    >
                      {INQUIRY_TOPICS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </FormField>
                </div>

                <FormField
                  label="諮詢內容"
                  required
                  error={errors.message}
                  style={{ marginTop: 16 }}
                >
                  <textarea
                    value={form.message}
                    onChange={(e) => setField('message', e.target.value)}
                    placeholder="請簡述您的需求，例如：作物別、預計申請的方案、希望取得驗證的時程等"
                    rows={5}
                    style={{
                      ...inputStyle(Boolean(errors.message)),
                      resize: 'vertical',
                      minHeight: 120,
                      lineHeight: 1.6,
                    }}
                  />
                </FormField>

                <div
                  style={{
                    marginTop: 24,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16,
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--stone-500)',
                      lineHeight: 1.55,
                    }}
                  >
                    送出即表示您同意我們以您提供的聯絡方式回覆相關諮詢。
                  </span>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 22px',
                      fontSize: 15,
                      fontWeight: 500,
                      color: 'var(--surface-base)',
                      background: submitting ? 'var(--forest-500)' : 'var(--forest-600)',
                      border: 'none',
                      borderRadius: 8,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      transition: 'all 150ms ease-out',
                      opacity: submitting ? 0.85 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!submitting) {
                        e.currentTarget.style.background = 'var(--forest-700)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!submitting) {
                        e.currentTarget.style.background = 'var(--forest-600)';
                      }
                    }}
                  >
                    <Send size={16} />
                    {submitting ? '送出中…' : '送出諮詢'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ helpers (local) ============ */

/** 左欄聯絡資訊單列：圖示 + 標籤 + 值 + hint。值若有 href 則包成連結。 */
function ContactInfoRow({ item }: { item: (typeof CONTACT_ITEMS)[number] }) {
  const Icon = item.icon;
  const valueNode = item.href ? (
    <a
      href={item.href}
      style={{
        fontSize: 16,
        fontWeight: 500,
        color: 'var(--stone-900)',
        textDecoration: 'none',
        transition: 'color 150ms ease-out',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--forest-700)')}
      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--stone-900)')}
    >
      {item.value}
    </a>
  ) : (
    <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--stone-900)' }}>{item.value}</span>
  );

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <span
        style={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'var(--forest-50)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={18} color="var(--forest-700)" strokeWidth={1.6} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--stone-500)',
            marginBottom: 4,
          }}
        >
          {item.label}
        </div>
        <div style={{ lineHeight: 1.5 }}>{valueNode}</div>
        {item.hint && (
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: 'var(--stone-500)',
              lineHeight: 1.55,
            }}
          >
            {item.hint}
          </div>
        )}
      </div>
    </div>
  );
}

/** 表單欄位包裝：統一 label / required 標記 / 錯誤訊息排版。 */
function FormField({
  label,
  required,
  error,
  children,
  style,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--stone-600)',
        }}
      >
        {label}
        {required && <span style={{ marginLeft: 4, color: 'var(--forest-700)' }}>*</span>}
      </span>
      {children}
      {error && (
        <span
          style={{
            fontSize: 12,
            color: '#b91c1c',
            lineHeight: 1.5,
          }}
        >
          {error}
        </span>
      )}
    </label>
  );
}

/** input / select / textarea 共用樣式；error 時改為紅色邊框。 */
function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: 'var(--font-body)',
    color: 'var(--stone-900)',
    background: 'var(--surface-base)',
    border: `1px solid ${hasError ? '#dc2626' : 'var(--stone-300)'}`,
    borderRadius: 8,
    outline: 'none',
    transition: 'border-color 150ms ease-out, box-shadow 150ms ease-out',
    boxSizing: 'border-box',
  };
}

/** 提交成功後的感謝視圖。提供「再寄一封」按鈕方便用戶連續提交。 */
function ThankYouCard({ onReset }: { onReset: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
        padding: '24px 4px',
      }}
    >
      <span
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: 'var(--forest-100)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckCircle2 size={24} color="var(--forest-700)" strokeWidth={1.6} />
      </span>
      <div>
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'var(--stone-900)',
            margin: '0 0 8px',
          }}
        >
          已收到您的諮詢
        </h3>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.65,
            color: 'var(--stone-600)',
            margin: 0,
            maxWidth: 480,
          }}
        >
          我們會在 1 個工作日內以您留下的 Email 或電話與您聯繫。
          若情況緊急，也歡迎直接撥打左側客服專線。
        </p>
      </div>
      <button
        type="button"
        onClick={onReset}
        style={{
          marginTop: 4,
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--stone-700)',
          background: 'transparent',
          border: '1px solid var(--stone-300)',
          borderRadius: 8,
          cursor: 'pointer',
          transition: 'all 150ms ease-out',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--stone-500)';
          e.currentTarget.style.color = 'var(--stone-900)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--stone-300)';
          e.currentTarget.style.color = 'var(--stone-700)';
        }}
      >
        再寄一封
      </button>
    </div>
  );
}
